
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Icon from 'common/components/elements/icons';
import GenerateColor from 'common/utils/generateTextColor.js';
import { useSiteGlobalSettingsHelper } from 'common/designer-modules/globalSettingsHelper';
import Link from 'common/components/modules/link';

const PageHeader = ({
   globalSettings,
   siteName,
   toggleMenu = () => {},
   sendTip = () => {},
   toggleChat = () => {},
   isAuth,
   logout = () => {},
   isNextApp,
   homepageHref,
   signInHref,
   pathname,
   isShowChat,
   unreadChatMessagesCount,
   isMenuOpened = false,
   primaryColor = '#231FFF',
}) => {
   const { getSiteLogoImage, getSectionGlobalSetting } = useSiteGlobalSettingsHelper(globalSettings);
   const headerSettings = getSectionGlobalSetting('header')
   const logo = getSiteLogoImage();
   const getPositionStyle = () => {
      if(headerSettings.header_scroll === 'sticky'){
         return {}
      }
      if(headerSettings.header_scroll === 'static'){
         return {
            position: isNextApp ? 'fixed' : 'absolute',
         }
      }
      if(headerSettings.header_scroll === 'disappear'){
         return {
            // position: isNextApp ? 'fixed' : 'absolute',
         }
      }
      return {}
   }
   return (
      <>
         <div
            style={ {
               ...getPositionStyle(),
               backgroundColor: GenerateColor.generateRGBColor(headerSettings.header_background_color, headerSettings.header_background_transparency),
               filter: `blur(${ headerSettings.header_background_blur }px)`,
            } }
            className='h-16 flex items-center gap-4 px-2 w-full top-0 right-0 z-[10]'>
            <PageHeaderButton
               icon={ isMenuOpened ? 'close' : 'menu' }
               buttonColor={ headerSettings.header_buttons_color }
               onClick={ toggleMenu }
               isActive={ isMenuOpened }
               primaryColor={ primaryColor }
               className='mms-siteMenu-open-close-button'
            />
            <div
               className={ cx(
                  {
                     'flex-1 flex h-10 items-center ': true,
                     // 'absolute top-0 left-0 w-full justify-center': 'center' === headerSettings.header_logo_aligment,
                     'justify-center': 'center' === headerSettings.header_logo_aligment,
                  }
               ) }
            >
               <Link href={ homepageHref } isNextApp>
                  {
                     logo && (
                        <img src={ logo } alt='logo' className='w-auto h-full object-cover object-center' />
                     )
                  }
                  {
                     !logo && (
                        <span
                           style={ {
                              color: headerSettings.header_buttons_color,
                           } }
                           className=' text-xl font-bold'>
                           {siteName}
                        </span>
                     )
                  }
               </Link>
            </div>
            <div className='flex gap-2 ml-auto'>
               {
                  !isAuth && (
                     <Link href={ signInHref } isNextApp prefetch className={ `link ${ pathname === signInHref ? 'text-action' : '' }` }>
                        <PageHeaderButton
                           icon='account'
                           text='Sign in'
                           buttonColor={ headerSettings.header_buttons_color }
                        />
                     </Link>
                  )
               }
               {
                  isAuth && (
                     <>
                        <PageHeaderButton
                           icon='sign-out'
                           buttonColor={ headerSettings.header_buttons_color }
                           onClick={ logout }
                        />
                        <PageHeaderButton
                           icon='tip'
                           buttonColor={ headerSettings.header_buttons_color }
                           onClick={ sendTip }
                        />
                        {
                           (!isNextApp || isShowChat) && (
                              <div
                                 className='relative cursor-pointer'
                                 role='presentation'
                                 onClick={ toggleChat }
                              >
                                 <PageHeaderButton
                                    icon='chat'
                                    buttonColor={ headerSettings.header_buttons_color }
                                    // onClick={ toggleChat }
                                 />
                                 {/* TODO_NEXT_CHAT maybe change this to badge */}
                                 {
                                    Boolean(unreadChatMessagesCount) && (
                                       <span
                                          className='w-fit whitespace-nowrap min-w-[20px] h-5 px-[5px] absolute top-0 left-5 bg-red dark:bg-red-dark text-gray-50 text-[11px] flex justify-center items-center rounded-full leading-5'
                                       >
                                          {unreadChatMessagesCount}
                                       </span>
                                    )
                                 }
                              </div>
                           )
                        }
                     </>
                  )
               }
               <PageHeaderButton
                  icon='help'
                  buttonColor={ headerSettings.header_buttons_color }
               />
            </div>
         </div>
         {
            headerSettings.header_scroll === 'static' && isNextApp && (
               <div className='h-14 w-full min-h-[56px]' />
            )
         }
      </>

   );
}

const PageHeaderButton = ({ // TODO NEED UPDATE NEW BUTTON COMPONENT
   text,
   icon,
   buttonColor,
   onClick,
   isActive = false,
   primaryColor,
   className = '',
}) => {
   const primaryBgColor = GenerateColor.generateRGBColor(primaryColor, 0.1)
   return (
      <button
         onClick={ onClick }
         className={ cx(
            {
               'flex items-center justify-center group hover:bg-hover rounded-label': true,
               'w-10 min-w-[40px]': !text,
               [className]: className,
            }
         ) }
         style={ {
            background: isActive ? primaryBgColor : '',
         } }
      >
         {
            icon && (
               <div className='flex items-center justify-center w-10 h-10'>
                  <Icon
                     name={ icon }
                     primaryColor={ isActive ? primaryColor : buttonColor }
                     size='xl'
                  />
               </div>
            )
         }
         {
            text && (
               <span
                  style={ {
                     color: buttonColor,
                  } }
                  className='text-base font-medium leading-[20px] hover:underline'
               >{text}</span>
            )
         }
      </button>
   )
}

PageHeader.propTypes = {
   globalSettings: PropTypes.object,
   isNextApp: PropTypes.bool,
   siteName: PropTypes.string,
   toggleMenu: PropTypes.func,
   sendTip: PropTypes.func,
   logout: PropTypes.func,
   toggleChat: PropTypes.func,
   isAuth: PropTypes.bool,
   homepageHref: PropTypes.string,
   signInHref: PropTypes.string,
   pathname: PropTypes.string,
   isShowChat: PropTypes.bool,
   unreadChatMessagesCount: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
   isMenuOpened: PropTypes.bool,
   primaryColor: PropTypes.string,
};

PageHeaderButton.propTypes = {
   text: PropTypes.string,
   icon: PropTypes.string,
   buttonColor: PropTypes.string,
   onClick: PropTypes.func,
   isActive: PropTypes.bool,
   primaryColor: PropTypes.string,
   className: PropTypes.string,
};

export default PageHeader;


